
import { defineComponent } from "vue";
import DataViewer from "@/components/drawers/bottom/DataViewer.vue";
import DisplayColumnSelector from "@/components/drawers/bottom/DisplayColumnSelector.vue";
import DrawerColumn from "@/components/drawers/bottom/DrawerColumn.vue";
import PageSizeSelector from "@/components/drawers/bottom/PageSizeSelector.vue";
import DatasetSelector from "@/components/shared/DatasetSelector.vue";
import { mapState } from "pinia";
import { useDataSource, useGammaData, useLayout, useMap } from "@/services";
import { GDT } from "@/types";
import { isEqual, snakeCase } from "lodash-es";
import { BButtonGroup } from "bootstrap-vue";
import { exportToCsv } from "@/util/helpers";

export default defineComponent({
  name: "TableDataViewer",
  components: {
    DatasetSelector,
    DataViewer,
    DisplayColumnSelector,
    DrawerColumn,
    PageSizeSelector,
    BButtonGroup,
  },
  props: {
    hasGroups: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    selection: [] as Record<string, string>[],
  }),
  computed: {
    ...mapState(useLayout, ["isBottomDrawerOpen"]),
    ...mapState(useMap, ["hasDataBoundary"]),
    ...mapState(useGammaData, ["activeSourceId"]),
    isVisible(): boolean {
      return useMap().getLayerVisibility(snakeCase(this.activeSourceId));
    },
    groupsController(): any {
      return this.hasGroups
        ? () => import("@/components/drawers/bottom/GroupsController.vue")
        : () => void 0;
    },
    useSpatialFilters(): boolean | GDT.DataFilter | null {
      const ds = useDataSource(this.activeSourceId)();
      return (
        (ds.usePolygonFilter && ds.polygonFilter) ||
        (ds.useScreenFilter && ds.screenFilter)
      );
    },
    spatialFilters(): GDT.DataFilter[] {
      return this.activeSourceId
        ? useDataSource(this.activeSourceId)().spatialFilters
        : [];
    },
    dataSources(): string[] {
      return useGammaData().dataSources;
    },
    currentPage: {
      get(): number {
        return this.activeSourceId
          ? useDataSource(this.activeSourceId)().paging.page
          : 1;
      },
      set(page: number) {
        if (this.activeSourceId) {
          const ds = useDataSource(this.activeSourceId)();
          ds.update("paging", {
            ...ds.paging,
            page,
          });
        }
      },
    },
    perPage(): number {
      return this.activeSourceId
        ? useDataSource(this.activeSourceId)().paging.size
        : 25;
    },
    totalRows(): number {
      return this.activeSourceId
        ? useDataSource(this.activeSourceId)().totalAvailableRows
        : 0;
    },
  },
  watch: {
    useSpatialFilters: {
      handler(newVal, oldVal) {
        if (!isEqual(newVal, oldVal)) {
          this.updateSpatialFilters();
        }
      },
      immediate: true,
    },
  },
  methods: {
    updateSelection(identifiers: Record<string, string>[]) {
      this.selection = identifiers;
    },
    updateSpatialFilters() {
      if (this.activeSourceId) {
        this.refreshActiveSourceData();
      }
    },
    refreshActiveSourceData() {
      if (this.activeSourceId) {
        if (this.currentPage !== 1) {
          this.currentPage = 1;
        } else {
          useDataSource(this.activeSourceId)().fetchData(
            {},
            { shouldUpdateData: true, requestType: "paged" }
          );
        }
      }
    },
    toggleLayerVisibility(lcId: string) {
      useMap().setLayerVisibility(snakeCase(lcId), !this.isVisible);
    },
    async exportSelection() {
      await exportToCsv(
        this.activeSourceId,
        await useDataSource(this.activeSourceId)().getAllRowsForExport()
      );
    },
  },
});
